/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import ReactCodeInput from "react-pin-input";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ConfirmationResult, UserCredential } from "firebase/auth";
import Functions from "../../Helpers/Functions";
import { FirebaseAuthMethodEnum } from "../../Enum/FirebaseAuthMethodEnum";
import { loginWithPhoneNumber } from "../../Services/FirebaseService";
import ButtonField from "../Ui/ButtonField";
import { useTailwindBrand } from "../shared";
import { useBrandAuthConfig } from "../../Hooks/Auth/useBrandAuthConfig";
import Teachr from "../../Objects/models/Teachr";
import Client from "../../Objects/models/Client";
import TeachrRepository from "../../Repositories/TeachrRepository";
import FormRadioYesNo from "../FormFields/FormRadioYesNo";
import { debugMode } from "../../Utils/Function";

interface Style {
  container: React.CSSProperties;
  inputStyle: React.CSSProperties;
  inputStyleInvalid: React.CSSProperties;
}

const style: Style = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "15px",
    height: "100%",
  },
  inputStyle: {
    fontFamily: "Nunito",
    fontWeight: "400",
    outline: "none",
    width: "100%",
    borderRadius: "15px",
    height: "100%",
    backgroundColor: "#F2F5FB",
    color: "#4E494A",
    border: " 0.5px solid ",
    borderColor: "rgba(128, 128, 128, 0.35)",
    textAlign: "center",
    margin: "0",
  },
  inputStyleInvalid: {
    fontFamily: "Nunito",
    fontWeight: "400",
    outline: "none",
    width: "100%",
    borderRadius: "15px",
    height: "100%",
    backgroundColor: "#F2F5FB",
    color: "#4E494A",
    border: " 0.5px solid ",
    borderColor: "#FF724F",
    textAlign: "center",
    margin: "0",
  },
};

interface CodeProps {
  phone: string;
  code: ConfirmationResult;
  handleSetAndRedirectUserConnect: (userCredential: UserCredential, authMethod: any, userObject?: Teachr | Client, isTeachr?: boolean) => void;
  setCodeDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUserNotFound: React.Dispatch<React.SetStateAction<boolean>>;
  setResendLoading: React.Dispatch<React.SetStateAction<boolean>>;
  userNotFound: boolean;
  resendLoading: boolean;
  handleCancel: () => void;
}

const Code: React.FC<CodeProps> = (props) => {
  const { phone, code, handleSetAndRedirectUserConnect, setCodeDialogOpen, setUserNotFound, setResendLoading, userNotFound, resendLoading, handleCancel } = props;
  const { classes } = useTailwindBrand();
  const [isPinCodeValid, setIsPinCodeValid] = useState<boolean>(true);
  const [currentCode, setCurrentCode] = useState<ConfirmationResult>(code);
  
  const [pendingCredential, setPendingCredential] = useState<UserCredential | null>(null);
  const [isTeachrValue, setIsTeachrValue] = useState<number>(0);
  const [isTeachrError, setIsTeachrError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const { getUserObject } = useBrandAuthConfig();

  const handleSubmit = (code: string): void => {
    currentCode
      .confirm(code)
      .then((result: UserCredential) => {
        TeachrRepository.getCurrentUser(result.user.uid)
          .then(() => {
            // User exists → redirect to dashboard
            setCodeDialogOpen(false);
            const userObject = getUserObject;
            userObject.phone = Functions.formatPhoneNumber(phone);
            handleSetAndRedirectUserConnect(result, FirebaseAuthMethodEnum.PHONE, userObject);
          })
          .catch((error: any) => {
            const errorCode = JSON.parse(JSON.stringify(error))?.data?.error;
            if (errorCode === "user not found") {
              setPendingCredential(result);
              setUserNotFound(true);
            }
          });
      })
      .catch((error: any) => {
        console.warn(error);
        setIsPinCodeValid(false);
      });
  };

  const handleRegister = () => {
    if (isTeachrValue === 0 && debugMode) {
      setIsTeachrError('Veuillez indiquer si vous êtes un enseignant');
      return;
    }
    if (!pendingCredential) return;
    setRegisterLoading(true);
    const userObject = getUserObject;
    userObject.phone = Functions.formatPhoneNumber(phone);
    handleSetAndRedirectUserConnect(
      pendingCredential,
      FirebaseAuthMethodEnum.PHONE,
      userObject,
      isTeachrValue === 1
    );
  };

  const handleResend = () => {
    setResendLoading(true);
    setIsPinCodeValid(true);
    loginWithPhoneNumber(Functions.formatPhoneNumber(phone))
      .then((newCode) => setCurrentCode(newCode))
      .finally(() => setResendLoading(false));
  };
 
  return (
    <div className="flex flex-col gap-6 lg:text-lg md:text-base text-sm">
      <div>
        <p className="block pb-4 overflow-hidden font-bold text-normal whitespace-nowrap text-ellipsis w-max">
          Un code vient d'être envoyé au{" "}
          {Functions.formatPhoneNumber(phone)}
        </p>
        <div className="w-full md:w-2/3 h-[45px] md:h-[53px]">
          <ReactCodeInput
            inputStyle={isPinCodeValid ? style.inputStyle : style.inputStyleInvalid}
            style={style.container}
            inputFocusStyle={{ borderColor: "#0054A3" }}
            length={6}
            onComplete={(value: string) => {
              handleSubmit(value);
            }}
          />
        </div>
      </div>

      {!isPinCodeValid && (
        <div className="flex items-center gap-1 text-sm text-third">
          <HighlightOffIcon />
          <p>
            Le code est invalide, renvoyez un nouveau code ou choisissez un
            autre moyen de connexion
          </p>
        </div>
      )}

      {userNotFound && (
        <div className="flex flex-col gap-4 p-4 rounded-lg border border-orange-200 bg-orange-50">
          <div className="flex items-start gap-2 text-orange-700">
            <InfoOutlinedIcon fontSize="small" className="mt-0.5 shrink-0" />
            <p className="text-sm font-medium">
              Aucun compte n'est associé à ce numéro. Souhaitez-vous créer un compte ?
            </p>
          </div>
          {debugMode &&
            <FormRadioYesNo
              label="Êtes-vous un enseignant ?"
              name="isTeachr"
              value={isTeachrValue}
              onChange={(val) => { setIsTeachrValue(val); setIsTeachrError(''); }}
              error={isTeachrError}
              required
            />}
          <div className="flex gap-3">
            <ButtonField
              label="S'inscrire"
              variant="contained"
              className="font-bold flex-1"
              onClick={handleRegister}
              loading={registerLoading}
              disabled={registerLoading}
            />
            <ButtonField
              label="Annuler"
              variant="outlined"
              className={`font-bold flex-1 bg-white ${classes.primaryText} ${classes.primaryBorder}`}
              onClick={() => handleCancel()}
              disabled={registerLoading}
            />
          </div>
        </div>
      )}

      {!userNotFound && (
        <div className="flex flex-col gap-2">
          <p className="text-normal">Pas de code ?</p>
          <ButtonField
            label="Renvoyer le code"
            variant="outlined"
            className={`w-1/2 font-bold ${classes.primaryText} ${classes.primaryBorder}`}
            onClick={handleResend}
            loading={resendLoading}
            disabled={resendLoading}
          />
        </div>
      )}
    </div>
  );
};

export default Code;
